
import React, { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, User, Send, Upload, XCircle, Image } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { analyzeContent } from "@/lib/api";

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  imageUrl?: string;
}

const PrivacyAIChat: React.FC = () => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "Hello! I'm your Privacy & Security Assistant. Ask me anything about online privacy, security best practices, or how to protect yourself from hacking and cyber threats. You can also upload images for security analysis.",
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [imageToUpload, setImageToUpload] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() && !imageToUpload) return;
    
    // Create a unique ID for the message
    const messageId = `msg-${Date.now()}`;
    let userContent = input.trim();
    let userImageUrl = null;
    
    // Handle image upload if present
    if (imageToUpload) {
      const reader = new FileReader();
      reader.readAsDataURL(imageToUpload);
      reader.onload = () => {
        userImageUrl = reader.result as string;
        
        // Add user message with image
        setMessages(prev => [
          ...prev, 
          {
            id: messageId,
            role: "user", 
            content: userContent || "Analyze this image for security concerns",
            imageUrl: userImageUrl,
            timestamp: new Date()
          }
        ]);
        
        // Process the response with both text and image
        processResponse(userContent, userImageUrl);
      };
    } else {
      // Add user message without image
      setMessages(prev => [
        ...prev, 
        {
          id: messageId,
          role: "user", 
          content: userContent,
          timestamp: new Date()
        }
      ]);
      
      // Process the response with just text
      processResponse(userContent);
    }
    
    // Clear input and image
    setInput("");
    setImageToUpload(null);
    setPreviewUrl(null);
  };
  
  const processResponse = async (userContent: string, imageBase64?: string | null) => {
    setIsLoading(true);
    
    try {
      // Get response through the API service
      const response = await analyzeContent({
        text: userContent ? `[PRIVACY AND SECURITY QUERY] ${userContent}` : undefined,
        image: imageBase64 || undefined
      });
      
      // Create a privacy-focused response
      const aiResponse = {
        id: `resp-${Date.now()}`,
        role: "assistant" as const,
        content: response?.explanation || "I couldn't process your request. Please try again.",
        timestamp: new Date()
      };
      
      // Add the response to messages
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error("Error getting AI response:", error);
      toast.error("Failed to get a response. Please try again later.");
      
      // Add error message
      setMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: "I'm sorry, I encountered an error while processing your request. Please try again later.",
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Only image files are supported");
      return;
    }
    
    // Validate file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }
    
    setImageToUpload(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  
  const cancelImageUpload = () => {
    setImageToUpload(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col h-[600px] border rounded-md">
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.role === "user"
                    ? "bg-truth text-white"
                    : "bg-gray-800 text-gray-100"
                }`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  {message.role === "user" ? (
                    <>
                      <span className="font-medium">You</span>
                      <User className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <Bot className="h-4 w-4" />
                      <span className="font-medium">Privacy Assistant</span>
                    </>
                  )}
                </div>
                
                {message.imageUrl && (
                  <div className="mb-2">
                    <img 
                      src={message.imageUrl} 
                      alt="Uploaded content" 
                      className="rounded-md max-h-64 max-w-full object-contain my-2"
                    />
                  </div>
                )}
                
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                
                <div className="text-xs opacity-70 mt-1">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-800 text-gray-100 rounded-lg p-4 max-w-[80%]">
                <div className="flex items-center space-x-2">
                  <Spinner size="sm" />
                  <p className="text-sm">Processing your request...</p>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {previewUrl && (
        <div className="px-4 py-2 bg-gray-900 border-t border-gray-700">
          <div className="relative inline-block">
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="h-16 rounded-md object-cover"
            />
            <button
              onClick={cancelImageUpload}
              className="absolute -top-2 -right-2 bg-gray-900 rounded-full"
            >
              <XCircle className="h-5 w-5 text-gray-400 hover:text-white" />
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-800">
        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about privacy, security, or hacking protection..."
              className="min-h-[60px] resize-none"
              disabled={isLoading}
            />
          </div>
          <div className="flex space-x-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={triggerFileInput}
              disabled={isLoading || !!imageToUpload}
              title="Upload image for analysis"
            >
              <Upload className="h-5 w-5" />
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || (!input.trim() && !imageToUpload)}
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PrivacyAIChat;
